import { useTranslation } from 'react-i18next';
import { Sun, Moon, Monitor } from 'lucide-react';

import { useTheme } from '@/hooks/useTheme';

/**
 * Theme toggle — 3-state segmented control (Light / System / Dark).
 *
 * Why three states vs. binary toggle :
 *   - Binary forces an opinion on people who don't have one
 *   - "System" (default) respects the OS-level preference, which the
 *     visitor has already configured once at the OS level
 *   - The user can still pin a specific theme if they want to override
 *
 * Mounted in Header (desktop) + MobileMenu (drawer). Local storage
 * persistence + cross-tab sync handled inside useTheme().
 *
 * @param {object} props
 * @param {string} [props.className]
 *
 * @example
 *   <ThemeToggle />
 */
export default function ThemeToggle({ className = '' }) {
  const { t } = useTranslation('components');
  const { choice, setChoice } = useTheme();

  const options = [
    { value: 'light', icon: Sun, label: t('themeToggle.light') },
    { value: 'system', icon: Monitor, label: t('themeToggle.system') },
    { value: 'dark', icon: Moon, label: t('themeToggle.dark') },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t('themeToggle.ariaLabel')}
      className={`inline-flex items-center rounded-full border border-navy-900/15 bg-cream-50 p-0.5 shadow-sm dark:border-cream-50/15 dark:bg-navy-800 ${className}`}
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        const active = choice === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setChoice(opt.value)}
            title={opt.label}
            aria-label={opt.label}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              active
                ? 'bg-bordeaux-600 text-cream-50 shadow-sm'
                : 'text-navy-900/60 hover:text-navy-900 dark:text-cream-50/60 dark:hover:text-cream-50'
            }`}
          >
            <Icon size={15} aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
